import { ChartBackground, ChartData } from '../types';
import {
  CHART_BG,
  CHART_FONT_SIZE,
  CHART_GRID,
  CHART_HEX,
  CHART_SAND_BG,
  CHART_SERIES_COLOR_IDS,
  CHART_TEXT,
  normalizeChart,
} from './chartModel';

export function chartColorHex(colorId: string, palette?: Record<string, string>): string {
  return palette?.[colorId] || CHART_HEX[colorId] || CHART_HEX.olympia;
}

function escapeXml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(value));
  const n = value / exp;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * exp;
}

function ticks(max: number): number[] {
  return [0, 0.5, 1].map((part) => Math.round(max * part * 100) / 100);
}

function backgroundFill(background: ChartBackground): string | null {
  if (background === 'white') return CHART_BG;
  if (background === 'sand') return CHART_SAND_BG;
  return null;
}

function legendBlock(
  items: Array<{ name: string; color: string }>,
  width: number,
  padL: number,
  padR: number,
  fontSize: number,
  text: string,
): { svg: string; height: number } {
  if (!items.length) return { svg: '', height: 0 };
  const maxX = width - padR;
  let x = padL;
  let y = 14;
  const rowH = Math.max(20, fontSize + 8);
  const parts: string[] = [];
  items.forEach((item) => {
    const labelW = Math.max(36, Math.min(160, item.name.length * fontSize * 0.58));
    const blockW = 16 + labelW;
    if (x + blockW > maxX && x > padL) {
      x = padL;
      y += rowH;
    }
    parts.push(
      `<rect x="${x}" y="${y - 10}" width="10" height="10" rx="2" fill="${item.color}"/>`
      + `<text x="${x + 16}" y="${y}" fill="${text}" font-size="${fontSize}" font-family="Akkurat Std, Arial">${escapeXml(item.name)}</text>`,
    );
    x += blockW + 18;
  });
  return { svg: parts.join(''), height: y + 10 };
}

export function renderChartSvg(input: {
  chart?: ChartData | null;
  width: number;
  height: number;
  palette?: Record<string, string>;
  gridColor?: string;
  textColor?: string;
  fontSize?: number;
}): string {
  const chart = normalizeChart(input.chart);
  const width = Math.max(80, Number(input.width) || 960);
  const height = Math.max(80, Number(input.height) || 400);
  const text = input.textColor || CHART_TEXT;
  const grid = input.gridColor || CHART_GRID;
  const fontSize = input.fontSize || CHART_FONT_SIZE;
  const colors = chart.series.map((item) => chartColorHex(item.colorId, input.palette));
  const pieColors = chart.categories.map((_, index) => (
    chartColorHex(chart.categoryColorIds[index] || CHART_SERIES_COLOR_IDS[index % CHART_SERIES_COLOR_IDS.length], input.palette)
  ));
  const legendItems = chart.kind === 'pie'
    ? chart.categories.map((name, index) => ({ name, color: pieColors[index] }))
    : chart.series.map((item, index) => ({ name: item.name, color: colors[index] }));
  const legend = chart.showLegend
    ? legendBlock(legendItems, width, 28, 20, fontSize, text)
    : { svg: '', height: 0 };
  const pad = { l: 64, r: 20, t: 18 + legend.height, b: 48 };
  const innerW = Math.max(20, width - pad.l - pad.r);
  const innerH = Math.max(20, height - pad.t - pad.b);

  let body = '';
  if (chart.kind === 'pie') {
    body = pieSvg(chart, pieColors, width, height, pad.t, fontSize, text);
  } else if (chart.kind === 'bar') {
    body = barSvg(chart, colors, pad, innerW, innerH, fontSize, text, grid);
  } else if (chart.kind === 'line') {
    body = lineSvg(chart, colors, pad, innerW, innerH, fontSize, text, grid);
  } else {
    body = columnSvg(chart, colors, pad, innerW, innerH, fontSize, text, grid);
  }

  const fill = backgroundFill(chart.background);
  const backdrop = fill
    ? `<rect width="${width}" height="${height}" fill="${fill}"/>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet">${backdrop}${legend.svg}${body}</svg>`;
}

function axisLabels(
  categories: string[],
  pad: { l: number; r: number; t: number; b: number },
  innerW: number,
  innerH: number,
  fontSize: number,
  text: string,
  vertical: boolean,
): string {
  return categories.map((label, index) => {
    if (vertical) {
      const y = pad.t + (index + 0.5) * (innerH / categories.length);
      return `<text x="${pad.l - 10}" y="${y + 4}" text-anchor="end" fill="${text}" font-size="${fontSize}" font-family="Akkurat Std, Arial">${escapeXml(label)}</text>`;
    }
    const x = pad.l + (index + 0.5) * (innerW / categories.length);
    return `<text x="${x}" y="${pad.t + innerH + 24}" text-anchor="middle" fill="${text}" font-size="${fontSize}" font-family="Akkurat Std, Arial">${escapeXml(label)}</text>`;
  }).join('');
}

function gridLines(
  max: number,
  pad: { l: number; t: number },
  innerW: number,
  innerH: number,
  fontSize: number,
  text: string,
  grid: string,
  horizontal: boolean,
): string {
  return ticks(max).map((tick) => {
    if (horizontal) {
      const y = pad.t + innerH - (tick / max) * innerH;
      return `<line x1="${pad.l}" y1="${y}" x2="${pad.l + innerW}" y2="${y}" stroke="${grid}" stroke-width="1" stroke-opacity="0.45"/>`
        + `<text x="${pad.l - 10}" y="${y + 4}" text-anchor="end" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${tick}</text>`;
    }
    const x = pad.l + (tick / max) * innerW;
    return `<line x1="${x}" y1="${pad.t}" x2="${x}" y2="${pad.t + innerH}" stroke="${grid}" stroke-width="1" stroke-opacity="0.45"/>`
      + `<text x="${x}" y="${pad.t + innerH + 24}" text-anchor="middle" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${tick}</text>`;
  }).join('');
}

function columnSvg(
  chart: ChartData,
  colors: string[],
  pad: { l: number; r: number; t: number; b: number },
  innerW: number,
  innerH: number,
  fontSize: number,
  text: string,
  grid: string,
): string {
  const values = chart.series.flatMap((item) => item.values);
  const max = niceMax(Math.max(0, ...values, 1));
  const groupW = innerW / chart.categories.length;
  const barW = Math.max(4, (groupW * 0.62) / chart.series.length);
  const bars = chart.categories.flatMap((_, catIndex) => chart.series.map((item, seriesIndex) => {
    const value = item.values[catIndex] || 0;
    const h = Math.abs(value) / max * innerH;
    const x = pad.l + catIndex * groupW + groupW * 0.19 + seriesIndex * barW;
    const y = value >= 0 ? pad.t + innerH - h : pad.t + innerH;
    const label = chart.showValues
      ? `<text x="${x + barW / 2}" y="${y - 5}" text-anchor="middle" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${value}</text>`
      : '';
    return `<rect x="${x}" y="${y}" width="${barW}" height="${Math.max(0, h)}" rx="3" fill="${colors[seriesIndex]}"/>${label}`;
  })).join('');
  return `${gridLines(max, pad, innerW, innerH, fontSize, text, grid, true)}${axisLabels(chart.categories, pad, innerW, innerH, fontSize, text, false)}${bars}`;
}

function barSvg(
  chart: ChartData,
  colors: string[],
  pad: { l: number; r: number; t: number; b: number },
  innerW: number,
  innerH: number,
  fontSize: number,
  text: string,
  grid: string,
): string {
  const values = chart.series.flatMap((item) => item.values);
  const max = niceMax(Math.max(0, ...values, 1));
  const groupH = innerH / chart.categories.length;
  const barH = Math.max(4, (groupH * 0.62) / chart.series.length);
  const bars = chart.categories.flatMap((_, catIndex) => chart.series.map((item, seriesIndex) => {
    const value = item.values[catIndex] || 0;
    const w = Math.abs(value) / max * innerW;
    const y = pad.t + catIndex * groupH + groupH * 0.19 + seriesIndex * barH;
    const x = value >= 0 ? pad.l : pad.l - w;
    const label = chart.showValues
      ? `<text x="${pad.l + w + 6}" y="${y + barH / 2 + 4}" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${value}</text>`
      : '';
    return `<rect x="${Math.max(pad.l, x)}" y="${y}" width="${Math.max(0, w)}" height="${barH}" rx="3" fill="${colors[seriesIndex]}"/>${label}`;
  })).join('');
  return `${gridLines(max, pad, innerW, innerH, fontSize, text, grid, false)}${axisLabels(chart.categories, pad, innerW, innerH, fontSize, text, true)}${bars}`;
}

function lineSvg(
  chart: ChartData,
  colors: string[],
  pad: { l: number; r: number; t: number; b: number },
  innerW: number,
  innerH: number,
  fontSize: number,
  text: string,
  grid: string,
): string {
  const values = chart.series.flatMap((item) => item.values);
  const max = niceMax(Math.max(0, ...values, 1));
  const step = chart.categories.length > 1 ? innerW / (chart.categories.length - 1) : innerW;
  const lines = chart.series.map((item, seriesIndex) => {
    const points = item.values.map((value, index) => {
      const x = pad.l + index * step;
      const y = pad.t + innerH - (value / max) * innerH;
      return { x, y, value };
    });
    const d = points.map((point, index) => `${index ? 'L' : 'M'}${point.x},${point.y}`).join(' ');
    const dots = points.map((point) => {
      const label = chart.showValues
        ? `<text x="${point.x}" y="${point.y - 8}" text-anchor="middle" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${point.value}</text>`
        : '';
      return `<circle cx="${point.x}" cy="${point.y}" r="3.5" fill="${colors[seriesIndex]}"/>${label}`;
    }).join('');
    return `<path d="${d}" fill="none" stroke="${colors[seriesIndex]}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>${dots}`;
  }).join('');
  return `${gridLines(max, pad, innerW, innerH, fontSize, text, grid, true)}${axisLabels(chart.categories, pad, innerW, innerH, fontSize, text, false)}${lines}`;
}

function pieSvg(
  chart: ChartData,
  colors: string[],
  width: number,
  height: number,
  top: number,
  fontSize: number,
  text: string,
): string {
  const values = (chart.series[0]?.values || []).map((value) => Math.max(0, value));
  const total = values.reduce((sum, value) => sum + value, 0);
  const cx = width / 2;
  const cy = (height + top) / 2;
  const r = Math.min(width, height - top) * 0.36;
  if (total <= 0) {
    return `<text x="${cx}" y="${cy}" text-anchor="middle" fill="${text}" font-size="${fontSize}" font-family="Akkurat Std, Arial">Inga värden</text>`;
  }
  let angle = -Math.PI / 2;
  return values.map((value, index) => {
    if (value <= 0) return '';
    const slice = (value / total) * Math.PI * 2;
    const start = angle;
    angle += slice;
    const x1 = cx + Math.cos(start) * r;
    const y1 = cy + Math.sin(start) * r;
    const x2 = cx + Math.cos(angle) * r;
    const y2 = cy + Math.sin(angle) * r;
    const large = slice > Math.PI ? 1 : 0;
    const mid = start + slice / 2;
    const lx = cx + Math.cos(mid) * (r * 0.58);
    const ly = cy + Math.sin(mid) * (r * 0.58);
    const label = chart.showValues
      ? `<text x="${lx}" y="${ly + 4}" text-anchor="middle" fill="${text}" font-size="${fontSize - 1}" font-family="Akkurat Std, Arial">${value}</text>`
      : '';
    return `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z" fill="${colors[index] || colors[0]}"/>${label}`;
  }).join('');
}
